/**
 * The Lev Learn family's shared class strings: the orange eyebrow, the text
 * measure, the section-hero title and tagline, the Try Lev button, and the hero
 * top padding.
 *
 * **Lives under `src/components`, not `src/lib`, on purpose.** Tailwind's
 * `content` globs scan `src/components` and `src/app` only, so a class string
 * that exists only in a `src/lib` file generates no CSS in a production build.
 * Dev hides it (the stylesheet is cached from an earlier literal), which is how
 * it shipped unnoticed once. A test (`learn.styles.test.ts`) fails if
 * this moves out of a scanned directory.
 *
 * No imports from the Learn chrome or anything that reads env: `LandingNav` (a
 * client component) imports this, and `LearnPageChrome` reads
 * `isVentureGraphPublic()`, whose env validation throws in the browser.
 */

/**
 * The Lev Learn brand mark: orange, uppercase, tracked.
 *
 * One class string because three places name the product — the header lockup
 * beside the wordmark, the index hero, and the creator page — and they had
 * drifted into three sizes and two colours, which is how a sub-brand stops
 * reading as one.
 *
 * **This is not the section-label style.** Grey 11px caps ("Related concepts",
 * "Browse by topic") label a block inside a page and are deliberately quieter;
 * so are the breadcrumbs, which are navigation rather than identity. Orange
 * caps mean "you are in Lev Learn" and nothing else — spend it sparingly or it
 * stops meaning anything.
 */
export const LEARN_EYEBROW =
  "text-[14px] font-bold uppercase tracking-[0.14em] text-lev-orange";

/**
 * The one text measure every block on a Learn page uses: title, answer-first
 * paragraph, and body prose all wrap at the same pixel.
 *
 * **It is pixels, not `ch`, and that is the whole point.** These blocks were
 * `max-w-[16ch]`, `max-w-[62ch]`, and `max-w-[68ch]`, which sounds consistent
 * and cannot be: a `ch` is a fraction of the font size, so the same count
 * resolves to 432px at 48px, 697px at 20px, and 611px at 17px. Three different
 * right edges down one page, with a shared left edge making the raggedness
 * obvious. No `ch` value can fix that, because the sizes differ by design.
 *
 * 640px was chosen against the corpus rather than by feel: it fits every concept
 * term up to ~26 characters on one line of the 48px H1 — including
 * "Sales Pipeline Stages" (457px) and "Ideal Customer Profile (ICP)" (593px),
 * both of which used to break mid-phrase — while keeping body prose at roughly
 * 75 characters per line, inside the comfortable reading range. The handful of
 * genuinely long terms ("Pay-for-Performance (Results-as-a-Service) Business
 * Model") still wrap, which is correct: the bug was a 21-character title
 * wrapping, not wrapping itself.
 *
 * Changing this number moves the right edge of every Learn page at once. That is
 * the intent — do not override it per-block.
 */
export const LEARN_MEASURE = "max-w-[640px]";

/**
 * The eyebrow, title and tagline of a section's landing hero: Concepts
 * (`/learn`), Guides and Venture Graph. One definition, so the three read as
 * one family and a size or weight change lands on all of them at once. The
 * eyebrow names the section; the title and tagline share the `LEARN_MEASURE`
 * right edge.
 */
export const LEARN_HERO_EYEBROW = `mb-3 ${LEARN_EYEBROW}`;
export const LEARN_HERO_TITLE = `mb-5 ${LEARN_MEASURE} text-[36px] leading-[1.08] tracking-[-0.96px] text-landing-dark md:text-[48px]`;
export const LEARN_HERO_TAGLINE = `${LEARN_MEASURE} text-lg leading-[1.5] text-landing-secondary md:text-xl`;

/**
 * The header button on every Lev Learn page: the same blue "Try Lev" pill on
 * Concepts, Guides, the concept pages and Venture Graph, rather than a Login on
 * some and a signup ask on others. Callers add their own layout (`ml-auto`,
 * `flex-shrink-0`).
 */
export const LEARN_TRY_LEV_CLASS =
  "rounded-full bg-landing-blue px-3.5 py-2 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90 sm:px-5 sm:py-2.5 sm:text-[13px]";

/**
 * Top padding of every Learn and Venture Graph hero. The nav bar is
 * `position: fixed`, so it takes no room in the page and the hero pads past it:
 * 96px on a phone, and on desktop 117px, which is the 85px bar plus 32px of air
 * before the section eyebrow (or breadcrumb on a detail page). One value, so
 * that gap cannot differ page to page.
 */
export const LEARN_HERO_PAD_TOP = "pt-24 md:pt-[117px]";
